import { Component, OnDestroy, OnInit } from '@angular/core';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { IonApp, IonRouterOutlet, Platform } from '@ionic/angular/standalone';
import { environment } from 'src/environments/environment';
import { AccountStatusRedirectService } from './services/account-status-redirect/account-status-redirect.service';
import { ApiService } from './services/api/api.service';
import { AppConfigService } from './services/app-config/app-config.service';
import { EventsService } from './services/events/events.service';
import { ModalsService } from './services/modals/modals.service';
import { PushWebService } from './services/push-web/push-web.service';
import { StorageService } from './services/storage/storage.service';
import { UtilsService } from './services/utils/utils.service';

interface DeferredInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly ANDROID_PROMPT_DISMISSED_KEY = 'pwa_install_prompt_android_dismissed';
  private static readonly IOS_TUTORIAL_DISMISSED_KEY = 'pwa_install_tutorial_ios_dismissed';

  public homologacao = false;
  private resumeListener: PluginListenerHandle | null = null;

  private exibindoModalFacaLogin = false;
  private exibindoModalNaoAutorizado = false;
  private installUxEmExibicao = false;
  private deferredInstallPromptEvent: DeferredInstallPromptEvent | null = null;
  versao: string | undefined;

  constructor(
    private readonly platform: Platform,
    private readonly storage: StorageService,
    private readonly events: EventsService,
    private readonly modalsService: ModalsService,
    private readonly utils: UtilsService,
    private readonly api: ApiService,
    private readonly appConfigService: AppConfigService,
    private readonly accountStatusRedirect: AccountStatusRedirectService,
    private readonly pushWeb: PushWebService
  ) {}

  async ngOnInit(): Promise<void> {
    this.homologacao = !environment.production;

    // Só a abertura fria pode travar para reautenticação; resume não trava.
    // A decisão lê apenas storage, então roda antes de qualquer await de
    // rede/SDK (OneSignal abaixo pode demorar ou travar na web).
    void this.restoreSessionRoute({ aberturaFria: true });

    void this.platform.ready().then(() => {
      if (this.platform.is('ios')) {
        document.documentElement.classList.add('boozap-ios');
      }
    });

    this.getVersao();

    void this.appConfigService.refreshAndPersist();

    if (Capacitor.isNativePlatform() && this.platform.is('capacitor')) {
      try {
        await SplashScreen.hide();
      } catch {
        // Plugin opcional
      }
    }

    this.events.subscribe('usuario:nao_autorizado', async () => {
      const sessao = await this.storage.get('jwt');
      if (!sessao) {
        this.exibirModalFacaLogin();
        return;
      }

      if (this.exibindoModalNaoAutorizado) {
        return;
      }
      this.exibindoModalNaoAutorizado = true;

      const modal = await this.modalsService.modalAlerta(
        'Sessão encerrada',
        'Sua sessão foi encerrada porque sua conta foi acessada em outro dispositivo.',
        'Ok'
      );
      await modal.onWillDismiss();
      this.exibindoModalNaoAutorizado = false;
      void this.utils.sair('/');
    });

    this.platform.ready().then(() => {
      this.utils.trocarCorStatusBar('#001b5e', 'LIGHT');
    });

    await this.pushWeb.configurarPush();
    const jwt = await this.storage.get('jwt');
    if (jwt) {
      await this.pushWeb.sincronizarTokenSeSessaoAtiva();
    }

    void this.registerResumeListener();

    await this.validarSessaoLocalAoIniciar();
    await this.setupPwaInstallUx();
  }

  ngOnDestroy(): void {
    void this.resumeListener?.remove();
    this.resumeListener = null;
  }

  private async registerResumeListener(): Promise<void> {
    this.resumeListener = await App.addListener('resume', () => {
      void this.pushWeb.sincronizarTokenSeSessaoAtiva();
      void this.restoreSessionRoute();
    });
  }

  private async restoreSessionRoute(options?: {
    aberturaFria?: boolean;
  }): Promise<void> {
    await this.accountStatusRedirect.navigateByStoredSession(undefined, {
      aberturaFria: options?.aberturaFria === true,
    });
  }

  getVersao(): void {
    void this.platform.ready().then(async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const appInfo = await App.getInfo();
          await this.storage.set('versao', appInfo.version);
          this.versao = appInfo.version;
        } catch {
          await this.storage.set('versao', 'web');
        }
      } else {
        await this.storage.set('versao', 'web');
      }
    });
  }

  exibirModalFacaLogin(): void {
    if (this.exibindoModalFacaLogin) {
      return;
    }
    this.exibindoModalFacaLogin = true;
    void this.modalsService
      .modalAlerta(
        'Se identifique para continuar',
        'Para continuar precisamos saber quem você é.',
        'Ok'
      )
      .then((modal) => {
        void modal.onWillDismiss().then(() => {
          this.exibindoModalFacaLogin = false;
          void this.utils.sair('/');
        });
      });
  }

  private async validarSessaoLocalAoIniciar(): Promise<void> {
    const jwt = await this.storage.get('jwt');
    const cliente = (await this.storage.get('cliente')) as Record<string, unknown> | null;

    if (!jwt) {
      return;
    }

    // Sessao incompleta no storage: limpa imediatamente.
    if (!cliente || !cliente['id']) {
      await this.utils.sair('/');
      return;
    }

    // Faz ping em endpoint protegido para invalidar sessao local stale.
    try {
      await this.api.get('assinaturasfamilia');
    } catch {
      // Interceptor trata 401 e dispara logout global.
    }
  }

  private async setupPwaInstallUx(): Promise<void> {
    if (Capacitor.isNativePlatform() || this.isStandaloneDisplayMode()) {
      return;
    }

    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.deferredInstallPromptEvent = event as DeferredInstallPromptEvent;
      void this.maybeShowPwaInstallUx();
    });

    window.addEventListener('appinstalled', () => {
      this.deferredInstallPromptEvent = null;
      void this.storage.set(AppComponent.ANDROID_PROMPT_DISMISSED_KEY, true);
      void this.storage.set(AppComponent.IOS_TUTORIAL_DISMISSED_KEY, true);
    });

    await this.maybeShowPwaInstallUx();
  }

  private async maybeShowPwaInstallUx(): Promise<void> {
    if (this.installUxEmExibicao || this.isStandaloneDisplayMode()) {
      return;
    }

    if (this.platform.is('ios')) {
      await this.showIosInstallTutorialIfNeeded();
      return;
    }

    if (this.deferredInstallPromptEvent) {
      await this.showAndroidInstallPromptIfNeeded();
    }
  }

  private async showAndroidInstallPromptIfNeeded(): Promise<void> {
    const dismissed = Boolean(await this.storage.get(AppComponent.ANDROID_PROMPT_DISMISSED_KEY));
    if (dismissed || !this.deferredInstallPromptEvent) {
      return;
    }

    this.installUxEmExibicao = true;
    try {
      const modal = await this.modalsService.modalAlerta(
        'Instalar Boozap Descontos',
        'Adicione à tela inicial para abrir mais rápido e usar como app no seu celular.',
        'Instalar',
        'Agora não',
        true,
      );

      const { data } = await modal.onDidDismiss<boolean>();
      if (data === true && this.deferredInstallPromptEvent) {
        await this.deferredInstallPromptEvent.prompt();
        const choice = await this.deferredInstallPromptEvent.userChoice;
        if (choice.outcome !== 'accepted') {
          await this.storage.set(AppComponent.ANDROID_PROMPT_DISMISSED_KEY, true);
        }
      } else {
        await this.storage.set(AppComponent.ANDROID_PROMPT_DISMISSED_KEY, true);
      }
    } finally {
      this.installUxEmExibicao = false;
    }
  }

  private async showIosInstallTutorialIfNeeded(): Promise<void> {
    const dismissed = Boolean(await this.storage.get(AppComponent.IOS_TUTORIAL_DISMISSED_KEY));
    if (dismissed) {
      return;
    }

    this.installUxEmExibicao = true;
    try {
      const modal = await this.modalsService.modalAlerta(
        'Instalar no iPhone',
        'Para usar como app e receber push: 1) toque em Compartilhar no Safari; 2) escolha Adicionar à Tela de Início; 3) abra pelo ícone. No Safari o push não é registrado.',
        'Entendi',
        'Não mostrar novamente',
        true,
      );

      const { data } = await modal.onDidDismiss<boolean>();
      if (data !== true) {
        await this.storage.set(AppComponent.IOS_TUTORIAL_DISMISSED_KEY, true);
      }
    } finally {
      this.installUxEmExibicao = false;
    }
  }

  private isStandaloneDisplayMode(): boolean {
    const navigatorStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    return navigatorStandalone || window.matchMedia('(display-mode: standalone)').matches;
  }
}
